/**
 * Derive the sibling storefront's host at the SAME deployment tier as the
 * current request, for the header site-switcher tabs (FSA <-> HSA).
 *
 * On production / staging brand domains this returns `null` so the caller
 * keeps the hardcoded production URL (which carries the `utm_source` /
 * `utm_medium` params marketing relies on). WDH has no sibling tab.
 *
 * Tiers handled (mirrors `cloudflare/worker.server.ts` SITE_MAP):
 *  - dev / dev-test / preview:  `hsa.devhec.com`, `hsatest.devhec.com`,
 *                               `preview.hsa.devhec.com`  (swap the site label)
 *  - per-site preview Workers:  `<prefix>-hsa.<account>.workers.dev`
 *                               (swap the `-<code>` token; the bare HSA-only
 *                               `hec.*.workers.dev` develop build has no
 *                               per-site sibling -> `null`)
 *  - local dev:                 `hsa.localhost[:port]`
 *
 * Returns an absolute URL (with trailing slash, then `search`) or `null` when
 * no same-tier sibling can be derived.
 *
 * `search` is the production tab's query string. Pass it so the remapped tab
 * keeps the COM-142 utm params: without them QA on devhec saw a bare
 * `https://hsa.devhec.com/` and could not verify the tab's tracking alongside
 * the COM-661 TPA params, which are added on top of whatever is here.
 */
export type SiteSwitchCode = "fsa" | "hsa";

// Site switcher tab URLs. The sibling (non-current) tab carries `code` so its
// href can be remapped to the same deployment tier on dev/preview hosts; the
// hardcoded production URL (with utm params) is the prod/staging fallback.
// The three utm params must match the legacy SFRA `tabbed-sites` Content Asset
// exactly (utm_medium=referral, utm_campaign=tabbed_experience) — the stores
// run separate GA4 properties, and anything else lands the session in the
// Unassigned channel (COM-142).
export const SITE_TABS: Record<
  string,
  { label: string; href: string; current: boolean; code?: SiteSwitchCode }[]
> = {
  hsa: [
    {
      label: "FSAstore.com",
      href: "https://fsastore.com/?utm_source=hsastore.com&utm_medium=referral&utm_campaign=tabbed_experience",
      current: false,
      code: "fsa",
    },
    { label: "HSAstore.com", href: "/", current: true },
  ],
  fsa: [
    { label: "FSAstore.com", href: "/", current: true },
    {
      label: "HSAstore.com",
      href: "https://hsastore.com/?utm_source=fsastore.com&utm_medium=referral&utm_campaign=tabbed_experience",
      current: false,
      code: "hsa",
    },
  ],
  wdh: [{ label: "WellDeservedHealth.com", href: "/", current: true }],
};

const OTHER: Record<SiteSwitchCode, SiteSwitchCode> = {
  fsa: "hsa",
  hsa: "fsa",
};

export function deriveSiblingHref(
  currentHost: string | undefined,
  siblingCode: SiteSwitchCode,
  search = "",
): string | null {
  if (!currentHost) return null;
  const [host, port] = currentHost.split(":");
  const lower = host.toLowerCase();
  const self = OTHER[siblingCode];

  // Production / staging brand domains -> keep the hardcoded prod link.
  if (lower.endsWith("store.com") || lower.endsWith("welldeservedhealth.com")) {
    return null;
  }

  let siblingHost: string | null = null;

  if (lower.endsWith(".devhec.com") || lower.endsWith(".localhost")) {
    siblingHost = swapLabel(host, self, siblingCode);
  } else if (lower.endsWith(".workers.dev")) {
    // Per-site preview Workers expose the site as a `-<code>` token; the bare
    // `hec.health-e-commerce.workers.dev` develop build is HSA-only and has no
    // per-site sibling, so it falls through to `null`.
    const m = lower.match(/-(fsa|hsa)\./);
    if (m && m[1] === self) {
      siblingHost = host.replace(`-${self}.`, `-${siblingCode}.`);
    }
  }

  if (!siblingHost) return null;
  const protocol = lower.endsWith(".localhost") ? "http" : "https";
  return `${protocol}://${siblingHost}${port ? `:${port}` : ""}/${search}`;
}

/**
 * Swap the three-letter site label at a host-label boundary, preserving a
 * `test` suffix and any `preview.` prefix:
 *   hsa.devhec.com         -> fsa.devhec.com
 *   hsatest.devhec.com     -> fsatest.devhec.com
 *   preview.hsa.devhec.com -> preview.fsa.devhec.com
 *   hsa.localhost          -> fsa.localhost
 */
function swapLabel(
  host: string,
  self: SiteSwitchCode,
  sibling: SiteSwitchCode,
): string | null {
  const re = new RegExp(`(^|\\.)${self}(test)?\\.`, "i");
  if (!re.test(host)) return null;
  return host.replace(
    re,
    (_m, pre: string, test?: string) => `${pre}${sibling}${test ?? ""}.`,
  );
}
